import type { TokenSource } from '../api/client';

export interface AuthSession {
  email: string;
  method: 'dev' | 'google' | 'g4sid' | 'password';
}

/** A password sign-in waiting for its TOTP code. The provider keeps the resolver. */
export interface MfaChallenge {
  /** Enrolled factor to answer (Identity Platform MultiFactorInfo.uid). */
  hintUid: string;
  displayName?: string;
}

/** A TOTP enrolment in progress. The provider keeps the SDK secret object. */
export interface TotpEnrolment {
  /** Base32 shared secret, for manual entry in the authenticator app. */
  secret: string;
  /** otpauth:// URL (opens the authenticator app on phones; can be shown as a QR). */
  otpauthUrl: string;
  codeLength: number;
}

export type PasswordResult = { status: 'signed-in' } | { status: 'mfa-required'; challenge: MfaChallenge };

/** Errors from the identity provider, normalised. */
export type AuthFlowCode =
  | 'INVALID_CREDENTIALS'
  | 'TOO_MANY_ATTEMPTS'
  | 'INVALID_CODE'
  | 'USER_DISABLED'
  | 'NETWORK'
  | 'NOT_CONFIGURED'
  /** The identity provider's blocking function refused the sign-in (e.g. not a Workspace account). */
  | 'BLOCKED'
  /** The operation needs a fresh sign-in (e.g. enrolment finished; sign in with the new code). */
  | 'REAUTH_REQUIRED'
  /** Not available on this platform (e.g. Google sign-in inside the store apps — see README). */
  | 'UNSUPPORTED'
  | 'UNKNOWN';

export class AuthFlowError extends Error {
  constructor(
    readonly code: AuthFlowCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'AuthFlowError';
  }
}

/**
 * Identity provider abstraction. Production uses Google Cloud Identity
 * Platform (ADR-0004/0016); development uses `dev:<email>` tokens that the
 * API accepts only when AUTH_MODE=dev.
 */
export interface AuthProvider extends TokenSource {
  readonly mode: 'dev' | 'identity-platform';
  current(): AuthSession | null;
  subscribe(listener: () => void): () => void;
  /** Starts Google sign-in (redirect; the page reloads). Restricted to the Workspace domain via `hd`. */
  /** `loginHint` (e.g. the identity IAP already verified) lets Google sign that account in without the chooser. */
  signInWithGoogle(opts?: { loginHint?: string }): Promise<void>;
  /** Starts G4SID (Zitadel OAuth / OIDC) sign-in using PKCE on web and system-browser relay on iOS/Android. */
  signInWithG4SId(opts?: { loginHint?: string }): Promise<void>;
  /** Waits for the persisted session to load and completes a pending Google or G4SID redirect; true if one completed. */
  completeRedirect(): Promise<boolean>;
  /** Completes a pending Google redirect on `/native-auth` and returns the raw Google OAuth tokens for native handoff. */
  completeNativeWebHandoff?(): Promise<{ idToken?: string; accessToken?: string } | null>;
  signInWithPassword(email: string, password: string): Promise<PasswordResult>;
  verifyMfa(challenge: MfaChallenge, code: string): Promise<void>;
  startTotpEnrolment(): Promise<TotpEnrolment>;
  finishTotpEnrolment(enrolment: TotpEnrolment, code: string): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  signOut(): Promise<void>;
  /** Dev mode only. */
  signInDev?(email: string, withMfa: boolean): Promise<void>;
}
